<template>
  <div class="c-site" :class="{ home: isHome }">
    <visitor-identification />
    <page-metadata :route="route" :canonicalUrl="canonicalUrl" />
    <facebook-metadata :route="route" />
    <twitter-metadata :route="route" />
    <structured-data-web-page :route="route" :canonicalUrl="canonicalUrl" />
    <structured-data-custom :route="route" />
    <div class="c-site__banner">
      <placeholder name="jss-banner" :rendering="route" />
    </div>
    <header class="c-site__header" role="banner">
      <div class="jss-placeholder">
        <placeholder name="jss-header" :rendering="route" />
      </div>
    </header>
    <main class="c-site__main" role="main">
      <div class="jss-placeholder c-site__breadcrumb">
        <placeholder name="jss-breadcrumb" :rendering="route" />
      </div>
      <div class="jss-placeholder c-site__content">
        <placeholder name="jss-main" :rendering="route" />
      </div>
      <div class="jss-placeholder c-site__infoHub">
        <placeholder name="jss-infohub" :rendering="route" />
      </div>
    </main>
    <footer class="c-site__footer" role="contentinfo">
      <div class="jss-placeholder">
        <placeholder name="jss-footer" :rendering="route" />
      </div>
    </footer>

    <ModalsContainer />

    <VueFinalModal
      v-model="fullwidthModal"
      class="final-modal fullwidthModal"
      content-class="final-modal-content"
      content-transition="vfm-fade"
      overlay-transition="vfm-fade"
    >
      <div class="c-modal">
        <div class="c-modal__content">
          <button class="c-modal__close" @click="fullwidthModal = false">X</button>
          <p>
            You will be linking to another website not owned or operated by CIS. CIS is not
            responsible for the availability or content of this website and does not represent
            either the linked website or you, should you enter into a transaction. The inclusion of
            any hyperlink does not imply any endorsement, investigation, verification or monitoring
            by CIS of any information in any hyperlinked site. We encourage you to review their
            privacy and security policies which may differ from CIS. If you "Proceed", the link will
            open in a new window.
          </p>
          <p class="proceed">
            <a
              v-if="link"
              :href="link"
              class="c-cta--secondary"
              target="_blank"
              rel="noopener noreferrer"
              @click="fullwidthModal = false"
            >
              Proceed
            </a>
          </p>
        </div>
      </div>
    </VueFinalModal>

    <VueFinalModal
      v-model="videoModal"
      class="final-modal videoModal"
      content-class="final-modal-content"
      content-transition="vfm-fade"
      overlay-transition="vfm-fade"
    >
      <div class="c-modal" :style="{ width: width + 'px' }">
        <div class="iframe-container">
          <iframe
            width="100%"
            height="100%"
            :src="videoSrc"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen
          ></iframe>
        </div>
        <button class="c-modal__close" @click="videoModal = false"></button>
      </div>
    </VueFinalModal>
  </div>
</template>

<script>
import { ref, defineComponent, getCurrentInstance } from 'vue';
import { Placeholder } from '@sitecore-jss/sitecore-jss-vue';
import { useWindowSize } from '@vueuse/core';
import { ModalsContainer, VueFinalModal } from 'vue-final-modal';
import VisitorIdentification from './VisitorIdentification';
import './assets/legacy/bootstrap/css/bootstrap.min.css';
import './assets/legacy/font-awesome/css/font-awesome.min.css';
import './assets/legacy/css/style.css';
import './assets/app.scss';
import PageMetadata from './PageMetadata.vue';
import FacebookMetadata from './FacebookMetadata.vue';
import TwitterMetadata from './TwitterMetadata.vue';
import StructuredDataWebPage from './StructuredDataWebPage.vue';
import StructuredDataCustom from './StructuredDataCustom.vue';

import './assets/legacy/retina/retina.min.js';

import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css'; // optional for styling

import 'vue-final-modal/style.css';

export default defineComponent({
  name: 'Layout',
  props: {
    route: {
      type: Object,
      default: () => ({})
    }
  },
  setup() {
    // Window Height
    const { width } = useWindowSize();
    // Fullwidth Modal
    const fullwidthModal = ref(false);
    // Large Modal
    const videoModal = ref(false);

    const instance = getCurrentInstance();
    const canonicalUrl =
      instance.appContext.config.globalProperties.$jss.sitecoreContext().canonicalUrl;

    return {
      width,
      fullwidthModal,
      videoModal,
      canonicalUrl
    };
  },
  data: function () {
    return {
      isHome: false,
      link: '',
      videoSrc: ''
    };
  },
  components: {
    VisitorIdentification,
    Placeholder,
    PageMetadata,
    FacebookMetadata,
    TwitterMetadata,
    StructuredDataWebPage,
    StructuredDataCustom,
    ModalsContainer,
    VueFinalModal
  },
  mounted() {
    if (this.route.name === 'home') {
      this.isHome = true;
    }

    tippy('[tooltip]', {
      content: (reference) =>
        reference.getAttribute('tooltip') + '<div class="tooltip-close">X</div>',
      allowHTML: true,
      interactive: true,
      interactiveDebounce: 75,
      maxWidth: 420,
      placement: 'top-start',
      touch: true,
      onCreate(instance) {
        instance.popper.querySelector('.tooltip-close').onclick = () => {
          instance.hide();
        };
      }
    });

    this.externalLinks();

    const videos = document.querySelectorAll('.youtube-video');
    if (videos) {
      videos.forEach((video) => video.addEventListener('click', this.openVideo));
    }
  },
  methods: {
    externalLinks() {
      let internalHostnames;

      try {
        internalHostnames = this.route.fields?.internalHostnamePatterns?.value
          ?.split(/\r?\n/)
          ?.filter((n) => n.length > 0)
          ?.map((n) => new RegExp(n, 'i'));
      } catch (err) {
        console.error('No Hostname', err);
      }

      if (internalHostnames && internalHostnames.length > 0) {
        // Only test http and https URLs, not mailto or others.
        const protocolPattern = /https?\:/i;

        // Get page links from DOM, converted into an array using the spread operator.
        const links = [...document.links];

        const externalLinks = links.filter(
          (link) =>
            protocolPattern.test(link.protocol) &&
            !internalHostnames.some((pattern) => pattern.test(link.hostname)) &&
            !link.classList.contains('youtube-video')
        );

        if (externalLinks) {
          externalLinks.forEach((link) => {
            return link.addEventListener('click', this.openDisclaimer);
          });
        }
      }
    },
    openDisclaimer(event) {
      event.preventDefault();
      if (event.target.href) {
        this.link = event.target.href;
        this.fullwidthModal = true;
      }
    },
    openVideo(event) {
      event.preventDefault();

      this.$nextTick(function () {
        if (event.target.href) {
          this.videoSrc = event.target.href + '?autoplay=1';
          this.videoModal = true;
        }
      });
    }
  }
});
</script>
<style lang="scss" scoped>
// Add any needed styles to the src\assets\scss\site.scss file
</style>
